import {
  UniformAssetTypeText,
  UniformMetadata,
} from "mannequin/src/types/tracking/mannequin/uniform";
import { SkeletalApi } from "../../../api/SkeletalApi";
import { Context } from "../../../poser/context";
import { fetchExtractJson } from "../../misc/util";
import { MaterialName, materialNames } from "./material";

export interface UniformVariantMap {
  textures: {
    [key: string]: string;
  };
}

function materialNameToUniformAsset(
  materialName: MaterialName
): UniformAssetTypeText | undefined {
  switch (materialName) {
    case "Cap":
      return "Cap";
    case "Jersey_Top":
      return "Jersey";
    case "Jersey_Bottom":
      return "Pants";
    default:
      return undefined;
  }
}

export class UniformVariants {
  variants: {
    [key: number]: Map<MaterialName, string>;
  };

  private variantMap: UniformVariantMap;
  private gameUniforms: UniformMetadata;

  private constructor(
    variantMap: UniformVariantMap,
    gameUniforms: UniformMetadata
  ) {
    this.variantMap = variantMap;
    this.gameUniforms = gameUniforms;

    this.variants = {
      [this.gameUniforms["home"].teamId]: this.makeVariants("home"),
      [this.gameUniforms["away"].teamId]: this.makeVariants("away"),
    };
  }

  private makeVariants(team: "home" | "away") {
    const variants = new Map<MaterialName, string>();

    for (const name of materialNames) {
      const override = this.getTextureVariant(team, name as MaterialName);
      if (override) {
        variants.set(name as MaterialName, override);
      }
    }

    return variants;
  }

  private getTextureVariant(
    team: "home" | "away",
    materialName: MaterialName
  ): string | undefined {
    const kind = materialNameToUniformAsset(materialName);
    if (kind === undefined) return undefined;

    const code = this.gameUniforms[team]?.items?.find(
      (i) => i.kind === kind
    )?.code;
    const texturePath = code ? this.variantMap.textures[code] : undefined;

    return texturePath && texturePath !== "" ? texturePath : undefined;
  }

  static async make(
    context: Context,
    skeletalApi: SkeletalApi
  ): Promise<UniformVariants | undefined> {
    try {
      const uniforms = await skeletalApi.fetchUniforms();
      const variants = await fetchVariants(context);

      if (uniforms && variants) {
        return new UniformVariants(variants, uniforms);
      } else {
        return undefined;
      }
    } catch (e) {
      console.warn("issue loading uniform variant data");
      return undefined;
    }
  }
}

async function fetchVariants(
  context: Context
): Promise<UniformVariantMap | undefined> {
  try {
    const response = await fetch(
      `${context.options.assetBase}/skins/materials/variants.json`
    );

    const variantData = (await fetchExtractJson(response)) as UniformVariantMap;
    if (variantData && variantData.textures) {
      return variantData;
    } else {
      console.error("blank or invalid material variant file");
      return undefined;
    }
  } catch (error) {
    console.error(error);
    return undefined;
  }
}
